import { Refresh } from "@mui/icons-material";
import {
  Box,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { DataTable, StatusPill, StyledCard } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { alertTypeLabelKey, ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThreatModel } from "types";
import { dateTimeFormat, getCameraLabel } from "utils";
import ThreatActions from "./actions";
import ExportEvents from "./export";
import UnresolvedThreats from "./unresolved";

interface Data {
  threats?: ThreatModel[];
  totalRecords?: number;
}

interface IThreatsTable {
  boxId?: string;
  siteId?: string;
  title?: string;
  hideExport?: boolean;
}

export default function ThreatsTable({ boxId, siteId, title, hideExport }: IThreatsTable) {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState<string>();
  const [isAscending, setAscending] = useState(false);
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const t = useTranslation();
  const { threats, totalRecords } = useMemo(() => {
    const data = response && response.data;
    if (data) {
      return data;
    }
    return {};
  }, [response]);
  const onSort = useCallback(
    (val: string) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const prevTotalRef = useRef<number | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio("/assets/audio/bell.wav");
  }, []);

  useEffect(() => {
    if (totalRecords !== undefined && prevTotalRef.current !== undefined && totalRecords > prevTotalRef.current) {
      audioRef.current?.play().catch(() => {});
    }
    prevTotalRef.current = totalRecords;
  }, [totalRecords]);

  useEffect(() => {
    const interval = setInterval(onReload, 30000);
    return () => clearInterval(interval);
  }, [onReload]);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!boxId) return;
    let params = `?limit=${limit}&page=${page + 1}&boxId=${boxId}`;
    if (sort) {
      if (isAscending) {
        params += `&sort=${sort}`;
      } else {
        params += `&sort=-${sort}`;
      }
    }

    callFetchGet(ENDPOINTS.threats + params).catch(console.error);
  }, [page, limit, sort, isAscending, boxId, reload, callFetchGet]);

  useEffect(() => setPage(0), [boxId]);

  const columns = useMemo(
    () => [
      {
        header: t["Camera"],
        accessor: (row: ThreatModel) => (
          <Typography variant="body2" fontWeight={500} sx={{ color: COLORS.surface.primaryText }} noWrap>
            {getCameraLabel(row.camera)}
          </Typography>
        )
      },
      {
        header: t["Time"],
        sort: "timestamp",
        accessor: (row: ThreatModel) => (
          <Typography variant="body2" sx={{ color: COLORS.surface.secondaryText }} noWrap>
            {dateTimeFormat(row.timestamp)}
          </Typography>
        )
      },
      {
        header: t["Event Type"],
        sort: "threatType.model",
        accessor: (row: ThreatModel) => {
          const { audio, video, model } = row.threatType;
          let label = "";
          if (audio && video) label = "Both";
          else if (audio) label = "Audio";
          else if (video) label = "Video";

          return (
            <Box display="inline-flex" alignItems="center" gap={1} sx={{ maxWidth: "100%", whiteSpace: "nowrap" }}>
              <Typography
                variant="body2"
                fontWeight={500}
                component="span"
                noWrap
                sx={{ color: COLORS.surface.primaryText }}
              >
                {t[alertTypeLabelKey(model)] || alertTypeLabelKey(model)}
              </Typography>
              {label ? (
                <>
                  <Typography component="span" sx={{ color: COLORS.surface.mutedText, fontSize: 14 }}>
                    •
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: "2px",
                      borderRadius: "6px",
                      border: `1px solid ${COLORS.surface.border}`,
                      bgcolor: COLORS.surface.card,
                      color: COLORS.surface.secondaryText,
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1.4
                    }}
                  >
                    {label}
                  </Box>
                </>
              ) : null}
            </Box>
          );
        }
      },
      {
        header: t["Status"],
        sort: "status",
        accessor: ({ status }: ThreatModel) => <StatusPill label={t[`${status}`]} status={status} />
      },
      {
        header: t["Actions"],
        accessor: (row: ThreatModel) => <ThreatActions threat={row} onSuccess={onReload} />
      }
    ],
    [t, onReload]
  );

  return (
    <StyledCard
      elevation={0}
      sx={{
        border: `1px solid ${COLORS.surface.border}`,
        borderRadius: `${COLORS.radius.lg}px`,
        boxShadow: COLORS.shadow.card,
        overflow: "hidden",
        bgcolor: COLORS.surface.card
      }}
    >
      <CardHeader
        title={title || t["Notifications"]}
        titleTypographyProps={{
          fontWeight: 600,
          letterSpacing: "-0.01em",
          sx: { color: COLORS.surface.primaryText, fontSize: 16 }
        }}
        sx={{
          px: 3,
          pt: 3,
          pb: 0
        }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ExportEvents siteId={siteId} boxId={boxId} />
            {isLoading ? (
              <IconButton size="small" disabled>
                <CircularProgress size={18} />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton size="small" onClick={onReload} sx={{ color: COLORS.surface.secondaryText }}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
      />
      <CardContent sx={{ px: 3, pt: 2.5, pb: 1.5, "&:last-child": { pb: 1.5 } }}>
        {!hideExport && <UnresolvedThreats key={reload + ""} />}
        <Box
          sx={{
            mt: hideExport ? 0 : 1,
            "& .MuiTableCell-root": {
              py: 1.5,
              px: 1.5,
              borderBottom: `1px solid ${COLORS.surface.border} !important`,
              fontSize: "0.875rem !important"
            },
            "& .MuiTableHead .MuiTableCell-root": {
              py: 1.25,
              bgcolor: `${COLORS.surface.tableHeader} !important`,
              borderBottom: "none !important"
            },
            "& .MuiTableHead .MuiTableRow-root .MuiTableCell-root:first-of-type": {
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8
            },
            "& .MuiTableHead .MuiTableRow-root .MuiTableCell-root:last-of-type": {
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8
            },
            "& .MuiTableHead .MuiTypography-root": {
              fontSize: "0.75rem !important",
              fontWeight: "600 !important",
              letterSpacing: "0",
              textTransform: "none",
              color: `${COLORS.surface.secondaryText} !important`
            },
            "& .MuiTableBody .MuiTableRow-root:last-of-type .MuiTableCell-root": {
              borderBottom: "none !important"
            },
            "& .MuiTableBody .MuiTableRow-root:hover": {
              bgcolor: "rgba(30, 34, 41, 0.02)"
            }
          }}
        >
          <DataTable
            rows={threats || null}
            isLoading={isLoading}
            error={error}
            sort={sort}
            isAscending={isAscending}
            onSort={onSort}
            columns={columns}
          />
        </Box>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: "flex-end",
          px: 3,
          py: 1.25,
          borderTop: `1px solid ${COLORS.surface.border}`
        }}
      >
        <StyledTablePagination
          component="div"
          align="right"
          count={totalRecords || 0}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </CardActions>
    </StyledCard>
  );
}
